import React, { useEffect, useState } from "react";
import {
    Badge, Button, Checkbox, CircularProgress, Grid, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, TextField, Tooltip, Typography, useTheme
} from "@mui/material";
import { connect } from "react-redux";
//--------------------------|| 3RD PARTY LIBRARIES ||------------------------------------//
import {
    IconCheck,
    IconEdit,
    IconFilter,
    IconPlaylistAdd,
    IconSortAscending2,
    IconSortDescending2,
    IconX
} from "@tabler/icons-react";
//--------------------------|| FILES IMPORT ||------------------------------------------//
import { getPager } from "helper/common";
import MainCard from "ui-component/cards/MainCard";
import SubCard from "ui-component/cards/SubCard";
import { NoData } from "ui-component/custom-components/nodata";
import { VWPagination } from "ui-component/custom-components/pagination";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import Chip from "ui-component/extended/Chip";
import moment from "moment";
import CreateAnticoccidial from "./create";
import FilterAnticoccidial from "./filter";
import {
    anticoccidalEdit,
    anticoccidalEditErrorClear,
    anticoccidalListErrorClear,
    clearAnticoccidalList,
    closeAnticoccidalCreateDialog,
    closeAnticoccidalFilterDialog,
    getAnticoccidalList,
    openAnticoccidalCreateDialog,
    openAnticoccidalFilterDialog
} from "store/app/form-management/anticoccidal-configuration/actions";
import { AllowedSpecialCharacters, AllowedSpecialCharactersPaste } from "helper/regex";

const tableHeaderStyle = { paddingLeft: '0px' }

const AnticoccidalList = (props) => {
    //--------------------List--------------------------------------
    const { anticoccidalList, totalCounts, isPending, errorMessage } = props;

    const [pageSize, setPageSize] = useState(10);
    const [Pager, setPager] = useState({ currentPage: 1 });
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortOrder, setSortOrder] = useState(null);

    //--------------------Filter------------------------------------
    const { anticoccidalFilterData, isAnticoccidalFilterDialogOpen } = props;
    //--------------------Create------------------------------------
    const { isAnticoccidalCreateDialogOpen } = props;
    //--------------------Edit--------------------------------------
    const { anticoccidalEditErrorMessage, isAnticoccidalEditPending } = props;

    const [isEdit, setIsEdit] = useState();
    const [updateAnticoccidial, setUpdateAnticoccidial] = useState();
    const [errors, setErrors] = useState();

    //--------------------Profile-----------------------------------
    const { userProfile } = props;
    const { userRoleName } = userProfile || {};

    //--------------------UI Design---------------------------------
    const theme = useTheme();
    const colorPrimary = theme.palette.secondary.dark;
    const [tableHeight, setTableHeight] = useState(window.innerHeight);

    //--------------------------------------------------------------
    useEffect(() => {
        props.anticoccidalListErrorClear();
        props.anticoccidalEditErrorClear();

        const handleResize = () => {
            setTableHeight(window.innerHeight)
        };
        window.addEventListener("resize", handleResize);
        return () => {
            props.clearAnticoccidalList();
            window.removeEventListener("resize", handleResize);
        }
    }, [])

    //--------------------------|| SORTING ||------------------------------------------//
    const handleSort = (column) => {
        setIsEdit();
        if (sortOrder === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortOrder(column);
            setSortDirection('asc');
        }
    };

    //--------------------------|| PAGINATION ||------------------------------------------//
    const setPage = (page = 1) => {
        setIsEdit();
        props.getAnticoccidalList(page, pageSize, sortOrder, sortDirection, anticoccidalFilterData);

        setPager(prevPager => {
            const totalPages = Math.ceil(totalCounts / pageSize);
            const visiblePages = 4;
            const pageOffset = Math.max(0, Math.floor((page - 1) / visiblePages) * visiblePages);
            const newPages = [];
            for (let i = pageOffset + 1; i <= Math.min(totalPages, pageOffset + visiblePages); i++) {
                newPages.push(i);
            }
            return {
                ...prevPager,
                currentPage: page,
                totalPages,
                pages: newPages,
            }
        })
    }

    useEffect(() => {
        const currentPager = getPager(totalCounts, 1, pageSize);
        setPager(currentPager)
    }, [totalCounts]);

    useEffect(() => {
        setPage();
    }, [anticoccidalFilterData, sortOrder, sortDirection, pageSize])

    //--------------------------|| FILTER ||------------------------------------------//
    const handleFilterOpen = () => {
        setIsEdit();
        props.openAnticoccidalFilterDialog();
    }
    const handleFilterClose = () => {
        props.closeAnticoccidalFilterDialog();
    }
    //--------------------------|| CREATE ||------------------------------------------//
    const handleRegionCreateOpen = () => {
        setIsEdit();
        props.openAnticoccidalCreateDialog();
    }
    const handleRegionCreateClose = () => {
        props.closeAnticoccidalCreateDialog();
    }

    //--------------------------|| EDIT ||------------------------------------------//
    const validateOnchange = (e) => {
        const { name, value, type, checked } = e.target;
        setUpdateAnticoccidial({
            ...updateAnticoccidial,
            [name]: type === "checkbox" ? checked : value,
        })
        setErrors({
            ...errors,
            [name]: null,
        })
    }

    const handleUpdateAnticoccidial = (e) => {
        e.preventDefault();

        const { anticoccidial } = updateAnticoccidial || {};
        const currentErrors = {};

        if (!anticoccidial && !anticoccidial?.trim()) {
            currentErrors.anticoccidial = 'Anticoccidial name is required';
        }
        setErrors(currentErrors);

        if (Object.keys(currentErrors).length === 0) {
            props.anticoccidalEdit(updateAnticoccidial, Pager && Pager.currentPage, pageSize, sortOrder, sortDirection, anticoccidalFilterData)
                .then((result) => {
                    if (result) {
                        setIsEdit();
                        setUpdateAnticoccidial();
                        setErrors();
                    }
                })
        }
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            handleUpdateAnticoccidial(e);
        }
        else {
            AllowedSpecialCharacters(e);
        }
    }
    return (
        <>
            {/*----------------------------|| HEADER ||--------------------------------*/}
            <SubCard sx={{
                p: 1.5,
                pl: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
            >
                <Grid item sx={{ display: 'flex', gap: { lg: 2, md: 2, sm: 1, xs: 1 }, alignItems: "center", marginRight: "10px" }}>
                    <Typography variant="h4">Anticoccidial List - </Typography>
                    <Tooltip title="Anticoccidial Count" placement="top">
                        <span>
                            <Chip
                                label={totalCounts}
                                chipcolor="secondary"
                                size="small"
                                sx={{
                                    borderRadius: "20%",
                                    py: 1,
                                    fontWeight: "bold",
                                    fontSize: 15,
                                    cursor: "pointer"
                                }}
                            />
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                    <Badge
                        badgeContent={anticoccidalFilterData ? Object.keys(anticoccidalFilterData).length : 0}
                        color="secondary"
                        invisible={anticoccidalFilterData ? Object.keys(anticoccidalFilterData).length === 0 : true}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleFilterOpen}
                            startIcon={<IconFilter />}
                        >
                            Filter
                        </Button>
                    </Badge>
                    {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleRegionCreateOpen}
                            startIcon={<IconPlaylistAdd />}
                        >
                            Create Anticoccidial
                        </Button>
                    }
                </Grid>
            </SubCard>
            {/*----------------------------|| TABLE ||--------------------------------*/}
            <MainCard sx={{ marginTop: '15px' }}>
                {errorMessage && <ErrorAlert type={'error'} content={errorMessage} />}
                {anticoccidalEditErrorMessage && <ErrorAlert type={'error'} content={`Update Anticoccidial Error : ${anticoccidalEditErrorMessage}`} />}
                <TableContainer sx={{ whiteSpace: "nowrap", maxHeight: `calc(${tableHeight}px - 380px)`, overflow: "auto" }}>
                    <Table size="small" stickyHeader aria-label="sticky table"
                        sx={{
                            '& tr > *:last-child': {
                                position: 'sticky',
                                right: 0,
                                bgcolor: 'white',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('Anticoccidial')}
                                        endIcon={sortOrder === 'Anticoccidial' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Anticoccidial
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('IsActive')}
                                        endIcon={sortOrder === 'IsActive' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Active ?
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('CreatedBy')}
                                        endIcon={sortOrder === 'CreatedBy' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Created By
                                    </Button>
                                </TableCell>
                                {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                                    <TableCell>
                                        Action
                                    </TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(anticoccidalList?.length > 0) && !isPending ?
                                anticoccidalList?.map((data, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            {isEdit === data.Id ?
                                                <TextField
                                                    autoFocus
                                                    autoComplete="off"
                                                    size="small"
                                                    placeholder="Enter anticoccidial name"
                                                    name="anticoccidial"
                                                    onChange={validateOnchange}
                                                    onKeyDown={handleKeyDown}
                                                    onPaste={AllowedSpecialCharactersPaste}
                                                    value={updateAnticoccidial?.anticoccidial || ""}
                                                    error={errors?.anticoccidial}
                                                    helperText={errors?.anticoccidial}
                                                />
                                                : (data.Anticoccidial || 'N/A')
                                            }
                                        </TableCell>
                                        <TableCell>
                                            {isEdit === data.Id ?
                                                <Checkbox
                                                    name="isActive"
                                                    onChange={validateOnchange}
                                                    checked={updateAnticoccidial?.isActive}
                                                    onKeyDown={handleKeyDown}
                                                />
                                                : (data.IsActive ?
                                                    <Chip label={"Active"} chipcolor="success" size="small" />
                                                    : <Chip label={"Inactive"} chipcolor="error" size="small" />
                                                )}
                                        </TableCell>
                                        <TableCell>{data.CreatedBy || 'N/A'}<br />
                                            <small>{data.CreatedTimeStamp ? moment(data.CreatedTimeStamp).format("DD MMM YYYY") : "N/A"}</small>
                                        </TableCell>
                                        {(userRoleName === "Administrator" || userRoleName === "Master admin") &&
                                            <TableCell>
                                                {isEdit === data.Id ?
                                                    isAnticoccidalEditPending ?
                                                        <Grid container alignItems={"center"} gap={1}>
                                                            <CircularProgress size={20} />
                                                            <Typography variant="h6" color={'primary'}> Loading...</Typography>
                                                        </Grid>
                                                        :
                                                        <Grid container columnGap={2}>
                                                            <Tooltip title="Submit" placement="top">
                                                                <IconCheck
                                                                    style={{ cursor: "pointer", color: "green" }}
                                                                    onClick={handleUpdateAnticoccidial}
                                                                />
                                                            </Tooltip>
                                                            <Tooltip title="Cancel" placement="top">
                                                                <IconX
                                                                    style={{ cursor: "pointer", color: "red" }}
                                                                    onClick={() => {
                                                                        setIsEdit();
                                                                        setUpdateAnticoccidial();
                                                                        setErrors();
                                                                        props.anticoccidalEditErrorClear();
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        </Grid>
                                                    : <Tooltip title="Edit Anticoccidial" placement="top">
                                                        <IconEdit style={{ cursor: "pointer" }} onClick={() => {
                                                            setIsEdit(data.Id);
                                                            setErrors();
                                                            setUpdateAnticoccidial({
                                                                anticoccidial: data.Anticoccidial,
                                                                isActive: data.IsActive,
                                                                id: data.Id
                                                            });
                                                        }} />
                                                    </Tooltip>
                                                }
                                            </TableCell>}
                                    </TableRow>
                                ))
                                /*--------|| LOADING ||---------*/
                                : isPending ?
                                    <TableRow >
                                        <TableCell colSpan={8} height='300px'>
                                            <Grid container alignItems={"center"} justifyContent={"center"} gap={1}
                                                sx={{
                                                    position: "sticky",
                                                    width: "fit-content",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)"
                                                }}
                                            >
                                                <CircularProgress />
                                                <Typography variant="h5" color={'primary'}> Loading...</Typography>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                    /*--------|| NO DATA ||---------*/
                                    : <TableCell colSpan={8} height='300px'>
                                        <NoData />
                                    </TableCell>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {/*--------|| PAGINATION ||---------*/}
                <Grid>
                    <VWPagination
                        pageDetails={Pager}
                        pageChange={setPage}
                        totalRecords={totalCounts}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                    />
                </Grid>
            </MainCard>
            <FilterAnticoccidial
                open={isAnticoccidalFilterDialogOpen}
                onClose={handleFilterClose}
                setPage={setPage}
            />
            <CreateAnticoccidial
                filter={anticoccidalFilterData}
                paginationData={{
                    page: (Pager && Pager.currentPage) || 1,
                    pageSize: pageSize,
                    sortDirection: sortDirection,
                    sortOrder: sortOrder
                }}
                open={isAnticoccidalCreateDialogOpen}
                onClose={handleRegionCreateClose}
            />
        </>
    )
}

const mapStateToProps = (state) => ({
    //--------------------List--------------------------------------
    anticoccidalList: state.anticoccidals.anticoccidalList,
    totalCounts: state.anticoccidals.anticoccidalListCount,
    isPending: state.anticoccidals.isAnticoccidalListPending,
    errorMessage: state.anticoccidals.anticoccidalListErrorMessage,
    //--------------------Filter------------------------------------
    isAnticoccidalFilterDialogOpen: state.anticoccidals.isAnticoccidalFilterDialogOpen,
    anticoccidalFilterData: state.anticoccidals.anticoccidalFilterData,
    //--------------------Create------------------------------------
    isAnticoccidalCreateDialogOpen: state.anticoccidals.isAnticoccidalCreateDialogOpen,
    //--------------------Edit--------------------------------------
    isAnticoccidalEditPending: state.anticoccidals.isAnticoccidalEditPending,
    anticoccidalEditErrorMessage: state.anticoccidals.anticoccidalEditErrorMessage,
    //--------------------Profile-----------------------------------
    userProfile: state.login.userProfile,
})

const mapDispatchToProps = (dispatch) => ({
    //--------------------List--------------------------------------
    getAnticoccidalList: (page, pageSize, sortOrder, sortDirection, filter) => dispatch(getAnticoccidalList(page, pageSize, sortOrder, sortDirection, filter)),
    clearAnticoccidalList: () => dispatch(clearAnticoccidalList()),
    anticoccidalListErrorClear: () => dispatch(anticoccidalListErrorClear()),
    //--------------------Filter------------------------------------
    openAnticoccidalFilterDialog: () => dispatch(openAnticoccidalFilterDialog()),
    closeAnticoccidalFilterDialog: () => dispatch(closeAnticoccidalFilterDialog()),
    setAnticoccidalFilteredData: (filter) => dispatch(setAnticoccidalFilteredData(filter)),
    //--------------------Create------------------------------------
    openAnticoccidalCreateDialog: () => dispatch(openAnticoccidalCreateDialog()),
    closeAnticoccidalCreateDialog: () => dispatch(closeAnticoccidalCreateDialog()),
    //--------------------Edit--------------------------------------
    anticoccidalEdit: (formData, page, pageSize, sortOrder, sortDirection, filter) => dispatch(anticoccidalEdit(formData, page, pageSize, sortOrder, sortDirection, filter)),
    anticoccidalEditErrorClear: () => dispatch(anticoccidalEditErrorClear()),
})

export default connect(mapStateToProps, mapDispatchToProps)(AnticoccidalList);